import React, { forwardRef, useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import Uploader from 'Components/Files/Uploader';
import Chip from '@material-ui/core/Chip';
import classNames from 'classnames';
import LinearProgress from 'Components/UI/LinearProgress';
import { getUploadText } from 'Helpers/File';
import RoundedButton from 'Components/ComponentLibrary/Atoms/Inputs/RoundedButton';

const useStyles = makeStyles(theme => ({
  root: {
    borderRadius: theme.radius.md,
    padding: theme.spacing(1, 1),
    display: 'flex',
    justifyContent: 'center',
    flexWrap: 'wrap',
  },
  button: {
    height: '48px',
  },
  chip: {
    flex: '1 1 100%',
    margin: theme.spacing(0.5, 1),
    position: 'relative',
    '& > svg': {
      position: 'absolute',
      right: 0,
    },
    maxWidth: '100%',
  },
  progress: {
    flex: 1,
  },
  progressChild: {
    margin: theme.spacing(0.5, 1),
    height: theme.spacing(4),
  },
}));

const UploadSection = forwardRef((
  {
    accept,
    content,
    className,
    onSuccess,
    onCatch,
    onDelete,
    method,
    url,
    documentResources,
    buttonTest,
    chipTest,
    onStart,
    component,
  },
  ref,
) => {
  const classes = useStyles();

  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleOnStart = async () => {
    if (onStart) {
      onStart();
    }
    setIsUploading(true);
  };

  if (documentResources && documentResources.length > 0) {
    return (
      <div className={classNames(classes.root, className)}>
        {documentResources.map(doc => (
          <Chip
            className={classes.chip}
            label={doc.attr('name')}
            onDelete={onDelete ? () => { onDelete(doc); } : undefined}
            color="primary"
            test={chipTest}
          />))}
      </div>
    );
  }

  const Component = component || (
    <RoundedButton
      ref={ref}
      color={'primary'}
      className={classes.button}
      test={buttonTest}
    >
      {content}
    </RoundedButton>
  );

  return (
    <div className={classNames(classes.root, className)}>
      <Uploader
        url={url}
        successToastMessage="Successfully saved document(s)"
        onSuccess={onSuccess}
        onCatch={onCatch}
        method={method}
        accept={accept}
        params={null}
        onStart={handleOnStart}
        onFinally={() => setIsUploading(false)}
        setUploadProgress={setUploadProgress}
      >
        {isUploading
          ? (
            <LinearProgress
              className={classes.progress}
              visible
              ProgressProps={{
                className: classes.progressChild,
                color: 'primary',
                value: uploadProgress,
                variant: 'determinate',
              }}
              text={getUploadText(uploadProgress)}
            />
          )
          : Component}
      </Uploader>
    </div>
  );
});

UploadSection.defaultProps = {
  content: 'Upload Document',
  onSuccess: () => {},
  onCatch: () => {},
  method: 'post',
  accept: 'image/x-png,image/png,image/jpeg',
  className: undefined,
  documentResources: undefined,
  onDelete: undefined,
  buttonTest: undefined,
  chipTest: undefined,
  onStart: undefined,
  component: undefined,
};

UploadSection.propTypes = {
  content: PropTypes.element,
  onSuccess: PropTypes.func,
  onDelete: PropTypes.func,
  onCatch: PropTypes.func,
  url: PropTypes.string.isRequired,
  method: PropTypes.string,
  accept: PropTypes.string,
  className: PropTypes.string,
  documentResources: PropTypes.arrayOf(PropTypes.object),
  buttonTest: PropTypes.string,
  chipTest: PropTypes.string,
  onStart: PropTypes.func,
  component: PropTypes.object,
};

export default UploadSection;
